
import lax from "lax.js";

import { library, dom } from "@fortawesome/fontawesome-svg-core";
import {
  faTwitter,
  faYoutube,
  faDiscord,
} from "@fortawesome/free-brands-svg-icons";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

import core, { Event, EventPlayer, TeamSlot } from "@/core";
import {
  defineComponent,
  ref,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
  nextTick,
  useContext,
  getCurrentInstance,
} from "@nuxtjs/composition-api";

import * as all from "@nuxtjs/composition-api";
import { devLog, removeFromArray } from "~/assets/js/helpers";

import { io } from "socket.io-client";
const socket = io({
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 4000,
});
export default defineComponent({
  name: "layout-default",
  setup(props, ctx) {
    const loaded = ref<boolean>(false);
    const error = ref<boolean>(false);
    const closeUpdate = ref<() => void>(null);
    const closeUpdateRemote = ref<() => void>(null);
    // useContext can throw if called outside a proper setup context (some SSR/runtime combos).
    // Wrap it defensively and fall back to getCurrentInstance().proxy when needed.
    let app: any = null;
    let redirect: any = null;
    let route: any = null;
    let nuxtCtx: any = null;
    try {
      nuxtCtx = useContext();
      app = nuxtCtx.app;
      redirect = nuxtCtx.redirect;
      route = nuxtCtx.route;
    } catch (e) {
      // fallback to instance proxy for SSR or when useContext isn't available
      const inst: any = getCurrentInstance() && getCurrentInstance().proxy;
      app = inst || null;
      redirect = inst ? inst.$router && inst.$router.push : null;
      route = inst ? inst.$route : null;
      nuxtCtx = inst || null;
    }
    const { $nuxt } = (getCurrentInstance() && getCurrentInstance().proxy) || { $nuxt: null };
    if (process.client) {
      // @ts-ignore
      const { $OneSignal } = (nuxtCtx as any) || useContext();
      onMounted(() => {
        if ($OneSignal.isPushNotificationsEnabled()) {
          devLog("Signed up for push notifications!").info(true);
        } else {
          devLog("Not signed up for push notifications!").info(true);
          $OneSignal.registerForPushNotifications();
        }
      });
    }
    onMounted(() => {
      library.add(faTwitter, faYoutube, faDiscord, fas);
      dom.watch();
      const phasesRan = [false, false];

      nextTick(async () => {
        try {
          $nuxt.$loading.start();
          const res = await core.user.getSession();

          phasesRan[1] = true;

          await core.events.actions
            .fetchEvents()
            .catch((err) => (error.value = true));
          phasesRan[0] = true;
        } catch (err) {
          console.warn(err);
          error.value = true;
          devLog(phasesRan).info();
          if (!phasesRan[1]) {
            redirect("/login", { loc: route.value.path });
          }
        } finally {
          $nuxt.$loading.finish();
          core.events.recompute();
          loaded.value = true;
        }
      });
    });
    onMounted(() => {
      closeUpdate.value = core.events.events.update.on(async (update) => {
        devLog(update).info(true);
        if (update.type === undefined) {
          update.type = "update";
        }
        if (update.persist === undefined) {
          update.persist = true;
        }
        try {
          const waitForSave = () => "";
          devLog("Emitting an update: ", update).info();
          $nuxt.$loading.start();
          socket.emit("update", update);

          window.addEventListener("beforeunload", waitForSave, false);
          if (update.persist) {
            if (update.payload.name || update.payload.status) {
              await core.events.updateEventDetails(
                update.eventId,
                update.payload
              );
            }
            if (
              update.payload.structure &&
              update.payload.slots &&
              update.type === "update"
            ) {
              await core.events.updateEventStructure(
                update.eventId,
                update.payload.slots,
                update.payload.structure
              );
            }
            if (update.payload.slots && update.type === "delete") {
              await core.events.removeEventSlots(
                update.eventId,
                update.payload.slots
              );
            }
            if (update.payload.players && update.type === "update") {
              await core.events.updateEventPlayers(
                update.eventId,
                update.payload.players
              );
            } else if (update.payload.players && update.type === "delete") {
              await core.events.removeEventPlayers(
                update.eventId,
                update.payload.players
              );
            }
          }

          window.removeEventListener("beforeunload", waitForSave);
        } catch (err) {
          console.error("Issue with updating...", err);
        } finally {
          $nuxt.$loading.finish();
        }
      });

      socket.on("update", async (update) => {
        devLog("recieved a remote update", update).info(true);

        const updatePlayers = (
          _updatedPlayers: EventPlayer[],
          type: "update" | "delete"
        ) => {
          _updatedPlayers.forEach(
            (plr) =>
              (core.events.collection.selectors.focused.value.players =
                removeFromArray(
                  core.events.collection.selectors.focused.value.players,
                  (player) => player.playerId === plr.playerId
                ))
          );

          const newPlayers = [
            ...core.events.collection.selectors.focused.value.players,
            ..._updatedPlayers,
          ];
          if (type === "delete") {
            _updatedPlayers.forEach((player) => {
              const arr = removeFromArray(
                newPlayers,
                (currPlayer: EventPlayer) =>
                  currPlayer.playerId === player.playerId
              );
              newPlayers.length = 0;
              Object.assign(newPlayers, arr);
            });

            devLog(newPlayers).info();
          }
          return newPlayers;
        };
        const updateSlots = (
          _updatedSlots: TeamSlot[],
          type: "update" | "delete"
        ) => {
          _updatedSlots.forEach(
            (slt) =>
              (core.events.collection.selectors.focused.value.slots =
                removeFromArray(
                  core.events.collection.selectors.focused.value.slots,
                  (slot) => slot.id === slt.id
                ))
          );
          const newSlots = [
            ..._updatedSlots,
            ...core.events.collection.selectors.focused.value.slots,
          ];
          if (type === "delete") {
            _updatedSlots.forEach((slot) => {
              const arr = removeFromArray(
                newSlots,
                (currSlot: TeamSlot) => currSlot.id === slot.id
              );
              newSlots.length = 0;
              Object.assign(newSlots, arr);
            });
          }
          return newSlots;
        };

        try {
          if (update.payload.players) {
            update.payload.players = updatePlayers(
              update.payload.players,
              update.type
            );
          }
          if (update.payload.slots) {
            update.payload.slots = updateSlots(
              update.payload.slots,
              update.type
            );
          }

          if (
            core.events.collection.selectors.focused.value.id === update.eventId
          ) {
            Object.assign(
              core.events.collection.selectors.focused.value,
              update.payload
            );
          } else {
            const event = await core.events.getEventById(update.eventId);
            if (event) {
              Object.assign(event, update.payload);
            } else {
              console.warn("Could not find the event to patch. Try reloading.");
            }
          }
          ctx.root.$forceUpdate();
          devLog(core.events.collection.selectors.focused.value).info();
        } catch (err) {
          console.error(err);
          devLog("Remote Update Failed").warn(true);
        } finally {
          devLog("Remote Update Success!").succ(true);

          core.events.recompute();
          core.events.patchEvent(update.eventId, update.payload);
          core.events.events.remote_update.emit(update);
        }
      });
    });
    onBeforeUnmount(() => {
      socket.close();
      if (closeUpdate.value) {
        closeUpdate.value();
      }
      if (closeUpdateRemote.value) {
        closeUpdateRemote.value();
      }
    });

    return {
      loaded,
      error,
    };
  },
});
